import { createQrSvg } from './fb-qr-svg.js';
import { calculateQrPrintMetrics } from './fb-qr-print-metrics.js';

class FeeedbackFreeQrTool extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-qr-form]');
    this.input = this.querySelector('[data-qr-input]');
    this.output = this.querySelector('[data-qr-output]');
    this.outputPanel = this.querySelector('[data-qr-output-panel]');
    this.placeholder = this.querySelector('[data-qr-placeholder]');
    this.downloadButton = this.querySelector('[data-qr-download]');
    this.svgDownloadButton = this.querySelector('[data-qr-svg-download]');
    this.cardDownloadButton = this.querySelector('[data-card-download]');
    this.businessInput = this.querySelector('[data-card-business]');
    this.formatSelect = this.querySelector('[data-card-format]');
    this.cardCanvas = this.querySelector('[data-card-output]');
    this.testLink = this.querySelector('[data-qr-test-link]');
    this.status = this.querySelector('[data-qr-status]');
    this.printWidth = this.querySelector('[data-qr-print-width]');
    this.printGrid = this.querySelector('[data-qr-print-grid]');
    this.printModuleSize = this.querySelector('[data-qr-print-module-size]');
    this.printQuietZone = this.querySelector('[data-qr-print-quiet-zone]');
    this.printDotsPerModule = this.querySelector('[data-qr-print-dots-per-module]');

    if (
      !this.form ||
      !this.input ||
      !this.output ||
      !this.downloadButton ||
      !this.svgDownloadButton ||
      !this.cardDownloadButton ||
      !this.businessInput ||
      !this.formatSelect ||
      !this.cardCanvas
    ) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.generate();
    });
    this.input.addEventListener('input', () => this.clearError());
    this.businessInput.addEventListener('input', () => this.refreshCard());
    this.formatSelect.addEventListener('change', () => this.refreshCard());
    this.downloadButton.addEventListener('click', () => this.downloadQrCode());
    this.svgDownloadButton.addEventListener('click', () => this.downloadQrSvg());
    this.cardDownloadButton.addEventListener('click', () => this.downloadCard());
  }

  clearError() {
    this.input.removeAttribute('aria-invalid');
    if (this.status?.dataset.state === 'error') {
      this.status.textContent = '';
      this.status.dataset.state = '';
    }
  }

  normalizedUrl() {
    const value = this.input.value.trim();
    if (!value) return null;

    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url.href;
    } catch {
      return null;
    }
  }

  async loadLibrary() {
    if (this.QRCode) return this.QRCode;
    const module = await import(this.dataset.qrCodeUrl);
    this.QRCode = module.QRCode;
    return this.QRCode;
  }

  async generate() {
    const url = this.normalizedUrl();
    if (!url) {
      this.input.setAttribute('aria-invalid', 'true');
      this.status.textContent = this.dataset.invalidMessage;
      this.status.dataset.state = 'error';
      this.input.focus();
      return;
    }

    this.clearError();
    this.status.textContent = this.dataset.loadingMessage;
    this.status.dataset.state = 'loading';
    this.downloadButton.disabled = true;
    this.svgDownloadButton.disabled = true;
    this.cardDownloadButton.disabled = true;

    try {
      const QRCode = await this.loadLibrary();
      this.output.replaceChildren();
      const qrCode = new QRCode(this.output, {
        text: url,
        width: 320,
        height: 320,
        colorDark: '#102a43',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H,
      });

      const qrCanvas = this.output.querySelector('canvas');
      if (!qrCanvas) throw new Error('QR canvas was not created');

      this.currentQrCanvas = qrCanvas;
      this.currentQrModel = qrCode._oQRCode;
      this.currentUrl = url;
      this.renderCard();

      this.outputPanel.hidden = false;
      this.placeholder.hidden = true;
      this.downloadButton.disabled = false;
      this.svgDownloadButton.disabled = false;
      this.cardDownloadButton.disabled = false;
      this.testLink.href = url;
      this.status.textContent = this.dataset.readyMessage;
      this.status.dataset.state = 'success';
    } catch {
      this.status.textContent = this.dataset.errorMessage;
      this.status.dataset.state = 'error';
    }
  }

  refreshCard() {
    if (!this.currentQrCanvas) return;
    this.renderCard();
  }

  selectedFormat() {
    if (this.formatSelect.value === 'business-card') {
      return {
        width: 650,
        height: 1004,
        physicalWidthMm: 55,
        filename: 'google-bewertungskarte-vorlage-visitenkarte.png',
      };
    }

    return {
      width: 1240,
      height: 1748,
      physicalWidthMm: 105,
      filename: 'google-bewertungskarte-vorlage-din-a6.png',
    };
  }

  roundRect(context, x, y, width, height, radius) {
    const safeRadius = Math.min(radius, width / 2, height / 2);
    context.beginPath();
    context.moveTo(x + safeRadius, y);
    context.arcTo(x + width, y, x + width, y + height, safeRadius);
    context.arcTo(x + width, y + height, x, y + height, safeRadius);
    context.arcTo(x, y + height, x, y, safeRadius);
    context.arcTo(x, y, x + width, y, safeRadius);
    context.closePath();
  }

  fitText(context, text, maxWidth, initialSize, minimumSize, weight = 700) {
    let size = initialSize;
    while (size > minimumSize) {
      context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      if (context.measureText(text).width <= maxWidth) break;
      size -= 1;
    }
    return size;
  }

  wrapText(context, text, x, y, maxWidth, lineHeight, maxLines = 3) {
    const words = text.trim().split(/\s+/);
    const lines = [];
    let line = '';

    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && context.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);

    const visibleLines = lines.slice(0, maxLines);
    if (lines.length > maxLines) {
      let finalLine = visibleLines[maxLines - 1];
      while (context.measureText(`${finalLine}…`).width > maxWidth && finalLine.length > 1) {
        finalLine = finalLine.slice(0, -1).trimEnd();
      }
      visibleLines[maxLines - 1] = `${finalLine}…`;
    }

    visibleLines.forEach((visibleLine, index) => {
      context.fillText(visibleLine, x, y + index * lineHeight);
    });

    return y + visibleLines.length * lineHeight;
  }

  renderCard() {
    const format = this.selectedFormat();
    const canvas = this.cardCanvas;
    const context = canvas.getContext('2d');
    if (!context || !this.currentQrCanvas) return;

    canvas.width = format.width;
    canvas.height = format.height;

    const scale = format.width / 650;
    const width = 650;
    const height = format.height / scale;
    const padding = 46;
    const contentWidth = width - padding * 2;
    const businessName = this.businessInput.value.trim() || this.dataset.cardBusinessFallback;

    context.save();
    context.scale(scale, scale);
    context.fillStyle = '#f4f8f5';
    context.fillRect(0, 0, width, height);
    context.fillStyle = '#0f5132';
    context.fillRect(0, 0, width, 14);

    context.textAlign = 'center';
    context.textBaseline = 'top';
    context.fillStyle = '#102a43';
    const businessSize = this.fitText(context, businessName, contentWidth, 34, 22, 750);
    context.font = `750 ${businessSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    context.fillText(businessName, width / 2, 54);

    context.fillStyle = '#0f6b43';
    context.font = '700 17px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    context.fillText(this.dataset.cardEyebrow.toLocaleUpperCase(), width / 2, 105);

    context.fillStyle = '#102a43';
    context.font = '750 48px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    let cursorY = this.wrapText(context, this.dataset.cardHeading, width / 2, 136, contentWidth, 53, 2);

    context.fillStyle = '#425466';
    context.font = '400 25px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    cursorY = this.wrapText(context, this.dataset.cardBody, width / 2, cursorY + 13, contentWidth - 24, 33, 3);

    const qrSize = Math.min(330, height * 0.355);
    const footerSpace = 92;
    const preferredQrY = cursorY + 24;
    const maximumQrY = height - qrSize - footerSpace;
    const qrY = Math.min(preferredQrY, maximumQrY);
    const qrX = (width - qrSize) / 2;

    context.fillStyle = '#ffffff';
    this.roundRect(context, qrX - 14, qrY - 14, qrSize + 28, qrSize + 28, 18);
    context.fill();
    context.imageSmoothingEnabled = false;
    context.drawImage(this.currentQrCanvas, qrX, qrY, qrSize, qrSize);

    context.fillStyle = '#102a43';
    context.font = '700 21px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    context.fillText(this.dataset.cardScan, width / 2, qrY + qrSize + 21);

    context.fillStyle = '#607284';
    context.font = '500 15px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    context.fillText(this.dataset.cardFooter, width / 2, height - 35);
    context.restore();

    canvas.setAttribute('aria-label', `${this.dataset.cardPreviewLabel}: ${businessName}`);
    this.updatePrintMetrics(format);
  }

  updatePrintMetrics(format) {
    if (!this.currentQrModel) return;

    const qrWidthInBaseLayout = 330;
    const baseLayoutWidth = 650;
    const printedQrWidthMm = format.physicalWidthMm * (qrWidthInBaseLayout / baseLayoutWidth);
    const metrics = calculateQrPrintMetrics(this.currentQrModel.getModuleCount(), printedQrWidthMm);
    if (!metrics) return;

    if (this.printWidth) this.printWidth.textContent = metrics.printedWidthMm.toLocaleString(undefined, { maximumFractionDigits: 1 });
    if (this.printGrid) this.printGrid.textContent = `${metrics.dataModules} × ${metrics.dataModules}`;
    if (this.printModuleSize) this.printModuleSize.textContent = metrics.moduleSizeMm.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (this.printQuietZone) this.printQuietZone.textContent = String(metrics.quietZone);
    if (this.printDotsPerModule) this.printDotsPerModule.textContent = metrics.dotsPerModule.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }

  triggerDownload(source, filename) {
    if (!source) return;

    const link = document.createElement('a');
    link.href = source;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  downloadQrCode() {
    const canvas = this.output.querySelector('canvas');
    const image = this.output.querySelector('img');
    const source = canvas?.toDataURL('image/png') || image?.src;
    this.triggerDownload(source, 'google-bewertungs-qr-code.png');
  }

  createQrSvg() {
    return createQrSvg(this.currentQrModel);
  }

  downloadQrSvg() {
    const svg = this.createQrSvg();
    if (!svg) return;

    const blobUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    this.triggerDownload(blobUrl, 'google-bewertungs-qr-code.svg');
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  }

  downloadCard() {
    if (!this.cardCanvas.width || !this.cardCanvas.height) return;
    this.triggerDownload(this.cardCanvas.toDataURL('image/png'), this.selectedFormat().filename);
  }
}

if (!customElements.get('fb-free-qr-tool')) {
  customElements.define('fb-free-qr-tool', FeeedbackFreeQrTool);
}
